import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { initializeApp } from "firebase/app";

// Firebase configuration
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  // measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Firebase Storage
const storage = getStorage(app);

// Storage configuration
const storageConfig = {
  maxFileSize: 5 * 1024 * 1024, // 5MB
  allowedTypes: ['image/jpeg', 'image/png', 'image/webp', 'image/gif'],
  uploadPath: 'product-images'
};

// Validate file before upload
export const validateFile = (file) => {
  const errors = [];
  
  // Check file size
  if (file.size > storageConfig.maxFileSize) {
    errors.push(`File size must be less than ${storageConfig.maxFileSize / (1024 * 1024)}MB`);
  }
  
  // Check file type
  if (!storageConfig.allowedTypes.includes(file.type)) {
    errors.push(`File type must be one of: ${storageConfig.allowedTypes.join(', ')}`);
  }
  
  return errors;
};

// Upload file to Firebase Storage
export const uploadFile = async (file, customPath = null) => {
  try {
    // Validate file
    const validationErrors = validateFile(file);
    if (validationErrors.length > 0) {
      throw new Error(validationErrors.join(', '));
    }
    
    // Check if we're in development mode
    const isDevelopment = process.env.NODE_ENV === 'development';
    
    if (isDevelopment) {
      // In development, compress and convert to base64 for better performance
      console.log('Development mode: Compressing and converting to base64');
      
      return new Promise((resolve, reject) => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();
        
        img.onload = () => {
          // Calculate new dimensions (max 800px width/height)
          const maxSize = 800;
          let { width, height } = img;
          
          if (width > height) {
            if (width > maxSize) {
              height = (height * maxSize) / width;
              width = maxSize;
            }
          } else {
            if (height > maxSize) {
              width = (width * maxSize) / height;
              height = maxSize;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          
          // Draw and compress
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to base64 with compression
          const compressedUrl = canvas.toDataURL('image/jpeg', 0.7);
          
          resolve({
            url: compressedUrl,
            path: `local/${Date.now()}-${file.name}`,
            name: file.name,
            size: file.size,
            type: file.type,
            isLocal: true
          });
        };
        
        img.onerror = reject;
        img.src = URL.createObjectURL(file);
      });
    }
    
    // Production: Try Firebase Storage upload
    const timestamp = Date.now();
    const fileName = `${timestamp}-${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
    const filePath = customPath || `${storageConfig.uploadPath}/${fileName}`;
    
    // Create storage reference
    const storageRef = ref(storage, filePath);
    
    // Upload file
    const snapshot = await uploadBytes(storageRef, file);
    
    // Get download URL
    const downloadURL = await getDownloadURL(snapshot.ref);
    
    return {
      url: downloadURL,
      path: filePath,
      name: fileName,
      size: file.size,
      type: file.type,
      isLocal: false
    };
    
  } catch (error) {
    console.error('Upload error:', error);
    
    // If Firebase upload fails, fall back to compressed base64 in development
    if (process.env.NODE_ENV === 'development') {
      console.log('Firebase upload failed, using compressed base64 fallback');
      
      return new Promise((resolve, reject) => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();
        
        img.onload = () => {
          // Calculate new dimensions (max 800px width/height)
          const maxSize = 800;
          let { width, height } = img;
          
          if (width > height) {
            if (width > maxSize) {
              height = (height * maxSize) / width;
              width = maxSize;
            }
          } else {
            if (height > maxSize) {
              width = (width * maxSize) / height;
              height = maxSize;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          
          // Draw and compress
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to base64 with compression
          const compressedUrl = canvas.toDataURL('image/jpeg', 0.7);
          
          resolve({
            url: compressedUrl,
            path: `local/${Date.now()}-${file.name}`,
            name: file.name,
            size: file.size,
            type: file.type,
            isLocal: true
          });
        };
        
        img.onerror = reject;
        img.src = URL.createObjectURL(file);
      });
    }
    
    // Handle specific Firebase errors
    if (error.code === 'storage/unauthorized') {
      throw new Error('Upload failed: Unauthorized access');
    } else if (error.code === 'storage/quota-exceeded') {
      throw new Error('Upload failed: Storage quota exceeded');
    } else if (error.code === 'storage/network-request-failed') {
      throw new Error('Upload failed: Network error');
    } else {
      throw new Error(`Upload failed: ${error.message}`);
    }
  }
};

// Delete file from Firebase Storage
export const deleteFile = async (fileUrl) => {
  try {
    // Handle local blob URLs
    if (fileUrl && fileUrl.startsWith('blob:')) {
      console.log('Revoking local blob URL');
      revokeLocalFileUrl(fileUrl);
      return;
    }
    
    // Handle base64 URLs (no cleanup needed)
    if (fileUrl && fileUrl.startsWith('data:')) {
      console.log('Base64 URL - no cleanup needed');
      return;
    }
    
    // Handle Firebase Storage URLs
    if (!fileUrl || !fileUrl.startsWith('https://firebasestorage.googleapis.com')) {
      console.log('Skipping deletion - not a Firebase Storage URL');
      return;
    }
    
    // Extract file path from URL
    const url = new URL(fileUrl);
    const path = decodeURIComponent(url.pathname.split('/o/')[1]?.split('?')[0] || '');
    
    if (!path) {
      throw new Error('Could not extract file path from URL');
    }
    
    const storageRef = ref(storage, path);
    await deleteObject(storageRef);
    
    console.log('File deleted successfully:', path);
    
  } catch (error) {
    console.error('Delete error:', error);
    
    // Don't throw error for deletion failures in development
    if (process.env.NODE_ENV === 'development') {
      console.log('Delete failed (development mode):', error.message);
      return;
    }
    
    throw new Error(`Delete failed: ${error.message}`);
  }
};

// Get file info from URL
export const getFileInfo = (fileUrl) => {
  try {
    if (!fileUrl) return null;
    
    const url = new URL(fileUrl);
    const path = decodeURIComponent(url.pathname.split('/o/')[1]?.split('?')[0] || '');
    
    return {
      url: fileUrl,
      path: path,
      name: path.split('/').pop(),
      isFirebaseUrl: fileUrl.startsWith('https://firebasestorage.googleapis.com')
    };
  } catch (error) {
    console.error('Error parsing file URL:', error);
    return null;
  }
};

// Development fallback for local file URLs
export const createLocalFileUrl = (file) => {
  if (!file) return null;
  
  try {
    return URL.createObjectURL(file);
  } catch (error) {
    console.error('Error creating local URL:', error);
    return null;
  }
};

// Clean up local URLs
export const revokeLocalFileUrl = (url) => {
  if (url && url.startsWith('blob:')) {
    try {
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error revoking local URL:', error);
    }
  }
};

export default storage; 